import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  AccountingOverview, CustomerDueRow, CollectionRow, LedgerEntry, LedgerFilters,
  getOverview, getCustomersDue, getTodaysCollections, getRecentLedger,
} from '../services/accountingApi';

interface State {
  overview: AccountingOverview | null;
  customersDue: CustomerDueRow[];
  collections: CollectionRow[];
  ledger: LedgerEntry[];
  ledgerFilters: LedgerFilters;
  ledgerLoading: boolean;
  ledgerError: string | null;
  loading: boolean;
  error: string | null;
  fetchAll: () => Promise<void>;
  setLedgerFilters: (patch: Partial<LedgerFilters>) => void;
  fetchLedger: () => Promise<void>;
}

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useAccountingStore = create<State>()(
  devtools(
    (set, get) => ({
      overview: null,
      customersDue: [],
      collections: [],
      ledger: [],
      ledgerFilters: {},
      ledgerLoading: false,
      ledgerError: null,
      loading: false,
      error: null,
      fetchAll: async () => {
        set({ loading: true, error: null });
        try {
          const [overview, customersDue, collections, ledger] = await Promise.all([
            getOverview(),
            getCustomersDue(25),
            getTodaysCollections(),
            getRecentLedger(50, get().ledgerFilters),
          ]);
          set({ overview, customersDue, collections, ledger, loading: false });
        } catch (err) { set({ loading: false, error: toErr(err) }); }
      },
      setLedgerFilters: (patch) => set({ ledgerFilters: { ...get().ledgerFilters, ...patch } }),
      // Sec 12 — ledger has real backend filter support (customer_id,
      // date_from, date_to); refetch just the ledger slice so filtering it
      // doesn't reload the rest of the Accounting page.
      fetchLedger: async () => {
        set({ ledgerLoading: true, ledgerError: null });
        try {
          const ledger = await getRecentLedger(50, get().ledgerFilters);
          set({ ledger, ledgerLoading: false });
        } catch (err) { set({ ledgerLoading: false, ledgerError: toErr(err) }); }
      },
    }),
    { name: 'accounting-store' }
  )
);
